import React, { useMemo } from 'react';
import { 
  FileText, 
  File, 
  Table, 
  Presentation,
  Image,
  Code,
  Archive,
  Video,
  Music,
  HelpCircle
} from 'lucide-react';

/**
 * File type categories derived from Microsoft Graph API mimeType and file extensions
 */
export type FileTypeCategory = 'office' | 'pdf' | 'image' | 'text' | 'spreadsheet' | 'presentation' | 'video' | 'audio' | 'archive' | 'code' | 'other';

/**
 * File type configuration with colors and icons
 */
interface FileTypeConfig {
  category: FileTypeCategory;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  label: string;
}

/**
 * Comprehensive file type mappings leveraging Microsoft Graph mimeType data
 */
const FILE_TYPE_CONFIGS: Record<FileTypeCategory, FileTypeConfig> = {
  office: {
    category: 'office',
    icon: FileText,
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Office Document'
  },
  pdf: {
    category: 'pdf',
    icon: FileText,
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'PDF Document'
  },
  spreadsheet: {
    category: 'spreadsheet',
    icon: Table,
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Spreadsheet'
  },
  presentation: {
    category: 'presentation',
    icon: Presentation,
    color: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Presentation'
  },
  image: {
    category: 'image',
    icon: Image,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    label: 'Image'
  },
  text: {
    category: 'text',
    icon: File,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    label: 'Text Document'
  },
  video: {
    category: 'video',
    icon: Video,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    label: 'Video'
  },
  audio: {
    category: 'audio',
    icon: Music,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    label: 'Audio'
  },
  archive: {
    category: 'archive',
    icon: Archive,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    label: 'Archive'
  },
  code: {
    category: 'code',
    icon: Code,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    label: 'Code'
  },
  other: {
    category: 'other',
    icon: HelpCircle,
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-50 dark:bg-slate-900/20',
    label: 'Other'
  }
};

/**
 * Determines file type category from Microsoft Graph API data
 * Uses mimeType primarily, with filename extension as fallback
 */
export const getFileTypeCategory = (fileName: string, mimeType?: string): FileTypeCategory => {
  // Normalize inputs
  const cleanFileName = fileName?.toLowerCase() || '';
  const cleanMimeType = mimeType?.toLowerCase() || '';

  // Microsoft Graph mimeType patterns (primary source)
  if (cleanMimeType) {
    // PDF documents
    if (cleanMimeType === 'application/pdf') {
      return 'pdf';
    }

    // Office documents (modern formats)
    if (cleanMimeType.includes('officedocument')) {
      if (cleanMimeType.includes('wordprocessingml')) return 'office';
      if (cleanMimeType.includes('spreadsheetml')) return 'spreadsheet';
      if (cleanMimeType.includes('presentationml')) return 'presentation';
    }

    // Office documents (legacy formats)
    if (cleanMimeType.includes('msword') || cleanMimeType === 'application/msword') {
      return 'office';
    }
    if (cleanMimeType.includes('ms-excel') || cleanMimeType === 'application/vnd.ms-excel') {
      return 'spreadsheet';
    }
    if (cleanMimeType.includes('ms-powerpoint') || cleanMimeType === 'application/vnd.ms-powerpoint') {
      return 'presentation';
    }

    // Images
    if (cleanMimeType.startsWith('image/')) {
      return 'image';
    }

    // Video
    if (cleanMimeType.startsWith('video/')) {
      return 'video';
    }

    // Audio
    if (cleanMimeType.startsWith('audio/')) {
      return 'audio';
    }

    // Text files
    if (cleanMimeType.startsWith('text/') || cleanMimeType === 'application/json') {
      return 'text';
    }

    // Archives
    if (cleanMimeType.includes('zip') || cleanMimeType.includes('archive') || 
        cleanMimeType.includes('compressed') || cleanMimeType.includes('tar')) {
      return 'archive';
    }
  }

  // Fallback: File extension analysis
  const extension = cleanFileName.split('.').pop() || '';

  // Office documents
  if (['doc', 'docx', 'odt', 'rtf'].includes(extension)) {
    return 'office';
  }

  // Spreadsheets
  if (['xls', 'xlsx', 'ods', 'csv'].includes(extension)) {
    return 'spreadsheet';
  }

  // Presentations
  if (['ppt', 'pptx', 'odp'].includes(extension)) {
    return 'presentation';
  }

  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif'].includes(extension)) {
    return 'image';
  }

  // Video
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'mpg', 'mpeg'].includes(extension)) {
    return 'video';
  }

  // Audio
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a'].includes(extension)) {
    return 'audio';
  }

  // Archives
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(extension)) {
    return 'archive';
  }

  // Code files
  if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'scss', 'json', 'xml', 'yaml', 'yml', 
       'py', 'java', 'c', 'cpp', 'cs', 'php', 'rb', 'go', 'rs', 'swift'].includes(extension)) {
    return 'code';
  }

  // Text files
  if (['txt', 'md', 'log', 'ini', 'cfg', 'conf'].includes(extension)) {
    return 'text';
  }

  return 'other';
};

/**
 * Props for FileTypeIcon component
 */
interface FileTypeIconProps {
  fileName: string;
  mimeType?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'badge' | 'pill';
  showLabel?: boolean;
  className?: string;
}

/**
 * FileTypeIcon Component
 * 
 * Displays appropriate icon and styling for file types based on Microsoft Graph API data.
 * Supports multiple display variants and sizes for different use cases.
 */
export const FileTypeIcon: React.FC<FileTypeIconProps> = ({
  fileName,
  mimeType,
  size = 'md',
  variant = 'icon',
  showLabel = false,
  className = ''
}) => {
  const fileTypeConfig = useMemo(() => {
    const category = getFileTypeCategory(fileName, mimeType);
    return FILE_TYPE_CONFIGS[category];
  }, [fileName, mimeType]);

  const IconComponent = fileTypeConfig.icon;

  // Size configurations
  const sizeConfig = {
    sm: {
      icon: 'h-4 w-4',
      text: 'text-xs',
      padding: 'px-1.5 py-0.5',
      gap: 'gap-1'
    },
    md: {
      icon: 'h-5 w-5',
      text: 'text-sm',
      padding: 'px-2 py-1',
      gap: 'gap-1.5'
    },
    lg: {
      icon: 'h-6 w-6',
      text: 'text-base',
      padding: 'px-3 py-1.5',
      gap: 'gap-2'
    }
  };

  const currentSize = sizeConfig[size];

  // Variant-specific rendering
  switch (variant) {
    case 'badge':
      return (
        <div className={`
          inline-flex items-center rounded-md
          ${currentSize.padding} ${currentSize.gap}
          ${fileTypeConfig.bgColor}
          border border-gray-200 dark:border-gray-600
          ${className}
        `}>
          <IconComponent className={`${currentSize.icon} ${fileTypeConfig.color}`} />
          {showLabel && (
            <span className={`font-medium ${fileTypeConfig.color} ${currentSize.text}`}>
              {fileTypeConfig.label}
            </span>
          )}
        </div>
      );

    case 'pill':
      return (
        <div className={`
          inline-flex items-center rounded-full
          ${currentSize.padding} ${currentSize.gap}
          ${fileTypeConfig.bgColor}
          ${className}
        `}>
          <IconComponent className={`${currentSize.icon} ${fileTypeConfig.color}`} />
          {showLabel && (
            <span className={`font-medium ${fileTypeConfig.color} ${currentSize.text}`}>
              {fileTypeConfig.label}
            </span>
          )}
        </div>
      );

    case 'icon':
    default:
      return (
        <div className={`inline-flex items-center ${currentSize.gap} ${className}`} title={fileTypeConfig.label}>
          <IconComponent 
            className={`${currentSize.icon} ${fileTypeConfig.color}`}
          />
          {showLabel && (
            <span className={`${fileTypeConfig.color} ${currentSize.text}`}>
              {fileTypeConfig.label}
            </span>
          )}
        </div>
      );
  }
};

/**
 * Hook to get file type information for use in other components
 */
export const useFileType = (fileName: string, mimeType?: string) => {
  return useMemo(() => {
    const category = getFileTypeCategory(fileName, mimeType);
    const config = FILE_TYPE_CONFIGS[category];
    
    return {
      category,
      config,
      canPreview: ['office', 'pdf', 'image', 'text'].includes(category),
      extension: fileName.toLowerCase().split('.').pop() || '',
      isOfficeDocument: category === 'office' || category === 'spreadsheet' || category === 'presentation'
    };
  }, [fileName, mimeType]);
};

export default FileTypeIcon;
